import * as React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import SmoothCollapse from 'react-smooth-collapse';
import { IframeWidthObserverFallbackWrapper, WidthObserver } from '@atlaskit/width-detector';
import { getElementHeight } from '@atlassian/help-center-common-util/dom';
import messages from './messages';
import { HelpText, minDescriptionHeight, ButtonText, StyledFakeButton } from './styled';

interface FieldDescriptionProps extends InjectedIntlProps {
    children: string | React.ReactNode;
}

interface FieldDescriptionState {
    shouldCollapse: boolean;
    isOpen: boolean;
}

/**
 * Will collapse a description if over two lines and show a "show more/less" button.
 */
export class FieldDescription extends React.PureComponent<FieldDescriptionProps, FieldDescriptionState> {
    state: FieldDescriptionState = {
        shouldCollapse: false,
        isOpen: false,
    };

    helpTextRef = React.createRef<HTMLElement>();

    componentDidMount() {
        this.checkIfShouldCollapse();
    }

    componentDidUpdate(prevProps: FieldDescriptionProps) {
        if (prevProps.children !== this.props.children) {
            this.checkIfShouldCollapse();
        }
    }

    checkIfShouldCollapse = () => {
        if (this.helpTextRef.current && getElementHeight(this.helpTextRef.current) > minDescriptionHeight) {
            this.setState({
                shouldCollapse: true,
            });
        } else if (this.state.shouldCollapse) {
            this.setState({
                shouldCollapse: false,
            });
        }
    };

    onToggle = (e: React.SyntheticEvent<HTMLDivElement>) => {
        e.preventDefault();
        this.setState((prevState) => ({
            isOpen: !prevState.isOpen,
        }));
    };

    renderHelpText = () => {
        const { children } = this.props;
        if (typeof children === 'string') {
            return <HelpText innerRef={this.helpTextRef} dangerouslySetInnerHTML={{ __html: children }} />;
        }
        return <HelpText innerRef={this.helpTextRef}>{children}</HelpText>;
    };

    render() {
        const { intl } = this.props;
        const { shouldCollapse, isOpen } = this.state;

        return (
            <>
                {/* IframeWidthObserverFallbackWrapper is needed for Edge 18. It can be removed when we no longer support Edge 18, as it does nothing in modern browsers. */}
                <IframeWidthObserverFallbackWrapper>
                    <WidthObserver setWidth={this.checkIfShouldCollapse} />
                </IframeWidthObserverFallbackWrapper>
                {shouldCollapse ? (
                    <StyledFakeButton onClick={this.onToggle}>
                        <SmoothCollapse expanded={isOpen} collapsedHeight={`${minDescriptionHeight}px`}>
                            {this.renderHelpText()}
                        </SmoothCollapse>

                        <ButtonText>{intl.formatMessage(isOpen ? messages.showLess : messages.showMore)}</ButtonText>
                    </StyledFakeButton>
                ) : (
                    this.renderHelpText()
                )}
            </>
        );
    }
}

export default injectIntl(FieldDescription);
